import React, { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import '../styles/SortableList.css';

interface SortableItemProps {
  id: string;
  value: string;
  onRemove: () => void;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, value, onRemove }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="sortable-item"
    >
      <div className="sortable-item-drag-handle" {...attributes} {...listeners}>
        <span className="drag-icon">⋮⋮</span>
      </div>
      <div className="sortable-item-content">
        {value}
      </div>
      <button
        type="button"
        className="sortable-item-remove"
        onClick={onRemove}
        title="Remove"
      >
        ✕
      </button>
    </div>
  );
};

interface SortableListProps {
  items: string[];
  onChange: (items: string[]) => void;
  placeholder?: string;
  addButtonText?: string;
  availableOptions?: string[]; // Predefined list to choose from
}

const SortableList: React.FC<SortableListProps> = ({
  items,
  onChange,
  placeholder = 'Enter item name...',
  addButtonText = 'Add Item',
  availableOptions
}) => {
  const [newItem, setNewItem] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [showCustomInput, setShowCustomInput] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = items.indexOf(active.id as string);
      const newIndex = items.indexOf(over.id as string);
      onChange(arrayMove(items, oldIndex, newIndex));
    }
  };

  const handleAdd = () => {
    const trimmed = newItem.trim();
    if (trimmed && !items.includes(trimmed)) {
      onChange([...items, trimmed]);
      setNewItem('');
    }
  };

  const handleAddSelected = () => {
    if (selectedOptions.length > 0) {
      const newItems = [...items, ...selectedOptions];
      onChange(newItems);
      setSelectedOptions([]);
    }
  };

  const handleRemove = (index: number) => {
    onChange(items.filter((_, i) => i !== index));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  const handleOptionToggle = (option: string) => {
    setSelectedOptions(prev =>
      prev.includes(option)
        ? prev.filter(o => o !== option)
        : [...prev, option]
    );
  };

  // Get available options that haven't been added yet
  const remainingOptions = availableOptions
    ? availableOptions.filter(option => !items.includes(option))
    : [];

  return (
    <div className="sortable-list">
      {availableOptions ? (
        <>
          {/* Predefined options with checkboxes */}
          {remainingOptions.length > 0 && (
            <div className="sortable-list-options">
              <div className="sortable-list-options-grid">
                {remainingOptions.map(option => (
                  <label key={option} className="sortable-list-option">
                    <input
                      type="checkbox"
                      checked={selectedOptions.includes(option)}
                      onChange={() => handleOptionToggle(option)}
                    />
                    <span>{option}</span>
                  </label>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleAddSelected}
                disabled={selectedOptions.length === 0}
                style={{ marginTop: '8px' }}
              >
                Add Selected ({selectedOptions.length})
              </button>
            </div>
          )}

          {/* Custom input toggle */}
          <div style={{ marginTop: '12px' }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setShowCustomInput(!showCustomInput)}
              style={{ fontSize: '12px' }}
            >
              {showCustomInput ? '− Hide Custom Input' : '+ Add Custom Space'}
            </button>
          </div>

          {/* Custom input */}
          {showCustomInput && (
            <div className="sortable-list-add" style={{ marginTop: '8px' }}>
              <input
                type="text"
                className="form-input"
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Enter custom space name..."
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleAdd}
                disabled={!newItem.trim()}
              >
                Add
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="sortable-list-add">
          <input
            type="text"
            className="form-input"
            value={newItem}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={placeholder}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={handleAdd}
            disabled={!newItem.trim()}
          >
            {addButtonText}
          </button>
        </div>
      )}

      {items.length > 0 ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={items} strategy={verticalListSortingStrategy}>
            <div className="sortable-list-items">
              {items.map((item, index) => (
                <SortableItem
                  key={item}
                  id={item}
                  value={item}
                  onRemove={() => handleRemove(index)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="sortable-list-empty">
          No items yet. Add one using the input above.
        </div>
      )}
    </div>
  );
};

export default SortableList;
